/**
 * La cuenta de un cliente: el cuaderno del fiado, hecho pantalla.
 *
 * What a client owes is kept by the reducers (`saldo` on the client), and that figure is the
 * one that counts. This lays out how it got there — what they owed from before, every fiado,
 * every abono, every return taken off the account, what the owner gave up on — oldest first with
 * the running balance, the way the notebook behind the counter always read.
 */

import { ZONA_HORARIA, formatMoney, type Centavos } from '@abastece/shared';
import { fiadoDeVenta, perdidoQueSigue, type Abono, type Cliente, type Devolucion, type Incobrable, type Venta } from '@abastece/core';
import { envolver, fila } from './recibo.js';

/**
 * `inicial`: what they already owed when the shop started here (`cliente.saldo_inicial`).
 * `perdido`: what the owner gave up on (`incobrable.registrado`).
 */
export type TipoMovimiento = 'inicial' | 'fiado' | 'abono' | 'devolucion' | 'perdido';

export interface Movimiento {
  id: string;
  tipo: TipoMovimiento;
  fecha: string;
  /** Ticket of the sale, for fiado and returns. */
  ticket: string | null;
  /** The sale itself, for fiado and returns: what the line opens on the client's page. */
  ventaId?: string;
  /** Positive adds to what they owe; negative takes it off. */
  monto: Centavos;
  /** What they owed once this was counted. */
  saldo: Centavos;
  /** For an abono: how it was paid, and the row itself, which can be voided. */
  abono?: Abono;
  /** For what was given up on: why, and the row itself, which can be undone. */
  incobrable?: Incobrable;
}

const ORDEN: Record<TipoMovimiento, number> = { inicial: 0, fiado: 1, devolucion: 2, abono: 3, perdido: 4 };

/**
 * Oldest first. Within a day the fiado comes before what paid it off: the projections keep the
 * day and not the hour, and a running balance that dipped below zero at noon because the abono
 * was listed before the sale would read as a mistake.
 *
 * What they owed from before goes first whatever its day, since it is from before everything
 * else: written down a week late, it is still the first thing the notebook said.
 */
export function estadoDeCuenta(
  ventas: readonly Venta[],
  abonos: readonly Abono[],
  devoluciones: readonly Devolucion[],
  inicial?: Pick<Cliente, 'saldoInicial' | 'saldoInicialFecha' | 'recuperado'>,
  incobrables: readonly Incobrable[] = [],
): Movimiento[] {
  const crudos: Omit<Movimiento, 'saldo'>[] = [];
  for (const v of ventas) {
    // A voided sale was never owed; the void took its fiado back off.
    if (v.anulada) continue;
    const fiado = fiadoDeVenta(v);
    if (fiado > 0) crudos.push({ id: v.id, tipo: 'fiado', fecha: v.fecha, ticket: v.ticket, ventaId: v.id, monto: fiado });
  }
  for (const d of devoluciones) {
    if (d.reembolso !== 'credito') continue;
    crudos.push({ id: d.id, tipo: 'devolucion', fecha: d.fecha, ticket: d.ticket, ventaId: d.ventaId, monto: -d.total as Centavos });
  }
  for (const a of abonos) {
    if (a.anulado) continue;
    crudos.push({ id: a.id, tipo: 'abono', fecha: a.fecha, ticket: null, monto: -a.monto as Centavos, abono: a });
  }
  // What still stands of each: undone, they owe it again, as if it had never been given up on;
  // what came back of it — paid, voided, returned — was not lost either (`perdidoQueSigue`).
  const sigue = perdidoQueSigue(incobrables, inicial?.recuperado);
  for (const x of incobrables) {
    const monto = sigue.get(x.id) ?? 0;
    if (monto > 0) crudos.push({ id: x.id, tipo: 'perdido', fecha: x.fecha, ticket: null, monto: -monto as Centavos, incobrable: x });
  }

  crudos.sort(
    (a, b) =>
      a.fecha.localeCompare(b.fecha) ||
      ORDEN[a.tipo] - ORDEN[b.tipo] ||
      (a.ticket ?? '').localeCompare(b.ticket ?? ''),
  );
  if (inicial?.saldoInicial && inicial.saldoInicialFecha) {
    crudos.unshift({
      id: 'inicial',
      tipo: 'inicial',
      fecha: inicial.saldoInicialFecha,
      ticket: null,
      monto: inicial.saldoInicial,
    });
  }

  let saldo = 0;
  return crudos.map((m) => {
    saldo += m.monto;
    return { ...m, saldo: saldo as Centavos };
  });
}

/**
 * What of each fiado is still owed, taking payments against the oldest first — the way everybody
 * settles a notebook: the day of each and what is left of it. Empty when nothing is owed.
 */
export function pendientesDelFiado(movimientos: readonly Movimiento[]): { fecha: string; resta: number }[] {
  const pendientes: { fecha: string; resta: number }[] = [];
  let aFavor = 0;
  for (const m of movimientos) {
    if (m.monto > 0) {
      // Money already in their favour pays for the next fiado first.
      const cubierto = Math.min(aFavor, m.monto);
      aFavor -= cubierto;
      if (m.monto - cubierto > 0) pendientes.push({ fecha: m.fecha, resta: m.monto - cubierto });
      continue;
    }
    let pago = -m.monto;
    while (pago > 0 && pendientes.length > 0) {
      const primero = pendientes[0]!;
      const toma = Math.min(pago, primero.resta);
      primero.resta -= toma;
      pago -= toma;
      if (primero.resta === 0) pendientes.shift();
    }
    aFavor += pago;
  }
  return pendientes;
}

/**
 * The day of the oldest fiado not yet paid off (`pendientesDelFiado`). Null when nothing is owed.
 * What was owed from before comes first but may carry a later day than the fiado after it, so this
 * is the earliest of the days still owed, not the first.
 */
export function debeDesde(movimientos: readonly Movimiento[]): string | null {
  return pendientesDelFiado(movimientos).reduce<string | null>((antes, p) => (antes === null || p.fecha < antes ? p.fecha : antes), null);
}

// ---------------------------------------------------------------------------
// Lo que se manda por WhatsApp o se imprime. En español, como el recibo: es para el cliente.
// ---------------------------------------------------------------------------

const ANCHO = 32;
const diaMes = (fecha: string) => `${fecha.slice(8, 10)}/${fecha.slice(5, 7)}`;
const hoyLargo = () =>
  new Date().toLocaleDateString('es-NI', { dateStyle: 'medium', timeZone: ZONA_HORARIA });

const CONCEPTO: Record<TipoMovimiento, (m: Movimiento) => string> = {
  inicial: () => 'Saldo anterior',
  fiado: (m) => `Fiado ${m.ticket ?? ''}`.trim(),
  // «Devol.»: with «Devolución» and an amount, thirty-two characters had no room for the whole
  // ticket, and the line was cut there. PR-000006 printed as PR-00000, another sale's number.
  devolucion: (m) => `Devol. ${m.ticket ?? ''}`.trim(),
  abono: () => 'Abono',
  perdido: () => 'Perdonado',
};

/** The last movements and what is owed, thirty-two characters wide. */
export function textoEstadoDeCuenta(
  negocio: string,
  cliente: { nombre: string; saldo?: Centavos | null },
  movimientos: readonly Movimiento[],
  cuantos = 15,
): string {
  const l: string[] = [];
  l.push(...envolver(negocio.toUpperCase()));
  l.push('ESTADO DE CUENTA');
  l.push(...envolver(cliente.nombre));
  l.push(`Al ${hoyLargo()}`);
  l.push('-'.repeat(ANCHO));
  const recientes = movimientos.slice(-cuantos);
  if (recientes.length < movimientos.length) l.push('(hay movimientos anteriores)');
  for (const m of recientes) {
    const monto = formatMoney(m.monto as Centavos, { symbol: false, signed: m.monto < 0 });
    const izq = `${diaMes(m.fecha)} ${CONCEPTO[m.tipo](m)}`;
    // Nothing is cut: an amount that does not fit beside its line goes on a line of its own.
    l.push(fila(izq, monto));
  }
  l.push('-'.repeat(ANCHO));
  const saldo = cliente.saldo ?? 0;
  l.push(
    saldo >= 0
      ? fila('DEBE', formatMoney(saldo as Centavos))
      : fila('SALDO A FAVOR', formatMoney(-saldo as Centavos)),
  );
  return l.join('\n');
}

/** A friendly nudge, for WhatsApp to the client's own number. */
export function textoRecordatorio(negocio: string, nombre: string, saldo: Centavos): string {
  return (
    `Hola, ${nombre}. Le saludamos de ${negocio}. ` +
    `Su saldo pendiente es de ${formatMoney(saldo)}. ` +
    'Cuando pueda pasar a abonar se lo agradecemos. ¡Que tenga buen día!'
  );
}

/** Proof of an abono, for the client to keep. */
export function textoAbono(
  negocio: string,
  cliente: { nombre: string; saldo?: Centavos | null },
  abono: Pick<Abono, 'monto' | 'fecha'>,
): string {
  const l: string[] = [];
  l.push(...envolver(negocio.toUpperCase()));
  l.push('COMPROBANTE DE ABONO');
  l.push(...envolver(cliente.nombre));
  l.push(
    new Date().toLocaleString('es-NI', { dateStyle: 'short', timeStyle: 'short', timeZone: ZONA_HORARIA }),
  );
  l.push('-'.repeat(ANCHO));
  l.push(fila('Abono', formatMoney(abono.monto)));
  const saldo = cliente.saldo ?? 0;
  l.push(
    saldo >= 0
      ? fila('Queda debiendo', formatMoney(saldo as Centavos))
      : fila('Saldo a favor', formatMoney(-saldo as Centavos)),
  );
  l.push('');
  l.push('¡Gracias!');
  return l.join('\n');
}
